import { Button } from '@/react/components/core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import { msg } from '@/services/isomorphic/I18NService';
import type { Callback, VoidCallback } from '@/type-utils';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent, FC, RefObject, KeyboardEvent } from 'react';

import S from './styles.base.module.scss';
import { search_box_title } from "@/lang/__generated__/ahnu/search_box_title";
import { search_box_placeholder } from "@/lang/__generated__/ahnu/search_box_placeholder";
import { search_box_clear } from "@/lang/__generated__/ahnu/search_box_clear";
import { search_box_search } from "@/lang/__generated__/ahnu/search_box_search";

interface ISearchModalInputProps {
  /**
   * The reference to the input element.
   *
   * This is used to focus the input when the modal is opened.
   */
  inputRef: RefObject<HTMLInputElement | null>;
  /**
   * The search box view model.
   */
  vm: SearchBoxVM;
  /**
   * A callback function to be called when the search navigation is performed.
   */
  onNavigate: VoidCallback;
  /**
   * A callback function to be called when the search is performed.
   */
  onSearch: Callback<[string], Promise<void>>;
  /**
   * A callback function to be called when the search input is cleared.
   */
  onClear: VoidCallback;
}

export const SearchModalInput: FC<ISearchModalInputProps> = observer(
  ({ inputRef, vm, onSearch, onClear, onNavigate }) => {
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      // Perform a debounced search on input change. The view model will be responsible for
      // maintain the state of the input value and perform the debounced search suggestions
      // based on the input value.
      onSearch(e.target.value);
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
      switch (e.key) {
        // On mobile, keyboard action button is mapped to "Enter" key
        case 'Enter':
          onNavigate();
          break;
        default:
          break;
      }
    };

    return (
      <form
        className={S.searchForm}
        role="search"
        onSubmit={(e) => e.preventDefault()}
      >
        <input
          ref={inputRef}
          className={S.searchInput}
          title={msg(search_box_title)}
          data-testid="plpSearchInput"
          aria-label={msg(search_box_placeholder)}
          placeholder={msg(search_box_placeholder)}
          value={vm.term}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
        />
        <div className={S.buttonsRow}>
          <Button
            className={S.clearButton}
            title={msg(search_box_clear)}
            datatestID="plpMobileModalClearSearchButton"
            variant="text"
            onClick={onClear}
            disabled={vm.term.length === 0}
            ariaLabel={msg(search_box_clear)}
          >
            <Icon
              className={S.formIcon}
              size={IconSizeProp.Size1X}
              icon={IconTypes.CircleXMark}
            />
          </Button>
          <Button
            className={S.searchButton}
            title={msg(search_box_search)}
            datatestID="plpMobileModalSearchButton"
            variant="text"
            onClick={onNavigate}
            disabled={vm.term.length === 0}
            ariaLabel={msg(search_box_search)}
          >
            <Icon
              className={S.formIcon}
              size={IconSizeProp.SizeLG}
              icon={IconTypes.Search}
            />
          </Button>
        </div>
      </form>
    );
  }
);
